import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { capitalize } from 'lodash-es';
import { escapeHtml } from '../../utils/html';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {
  HasPermissionDirective,
  HttpErrorResponse,
  isHttpErrorResponse,
  PermissionService,
  triggerIfSlow,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { ConfirmHandler } from '../../components/dialog/dialog.model';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { InteractionSelectionDialogService } from '../../components/interaction-selection-dialog/services/interaction-selection-dialog.service';
import {
  ReferencedDialogComponent,
  ReferencedDialogData,
} from '../../components/referenced-dialog/referenced-dialog.component';
import { TagFilterComponent } from '../../components/tag-filter/tag-filter.component';

import { GlobalLoadersService } from '../../global-loaders.service';
import { Interaction, InteractionStatus } from '../../models/interactions.model';
import { SecondsToDurationPipe } from '../../pipes/seconds-to-duration.pipe';
import { InteractionService } from '../../services/interaction.service';
import { TableLimitService } from '../../services/table-limit.service';
import { TagResponse, TagService } from '../../services/tag.service';
import { adjustPageIfNeeded } from '../../utils/pagination';
import { ScmFolderWarningService } from '../client-signaling/services/scm-folder-warning.service';

@Component({
  selector: 'app-client-interactions',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatPaginator,
    MatTableModule,
    MatTooltip,
    MatSort,
    MatSortHeader,
    DatePipe,
    DataTableContainerComponent,
    SecondsToDurationPipe,
    MatInput,
    MatFormField,
    MatPrefix,
    MatSuffix,
    FormsModule,
    TagFilterComponent,
    MatChipListbox,
    MatChipOption,
    HasPermissionDirective,
  ],
  templateUrl: './client-interactions.component.html',
  styleUrl: './client-interactions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientInteractionsComponent {
  private static readonly LOADER_CALLER_ID = 'client_interactions_loader_caller';

  private readonly _dialog = inject(DialogService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _interactionService = inject(InteractionService);
  private readonly _interactionSelectionService = inject(
    InteractionSelectionDialogService
  );
  private readonly _tagService = inject(TagService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _scmWarningService = inject(ScmFolderWarningService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _wsService = inject(WebSocketService);
  private readonly _snackbar = inject(MatSnackBar);

  private readonly _clientId$ = this._route.parent!.paramMap.pipe(
    map((params) => params.get('clientId'))
  );

  private readonly _fetch$ = new Subject<void>();

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(null);
  protected readonly includeShared = signal<boolean>(true);
  protected readonly selectedTagIds = signal<string[]>([]);
  protected readonly filterTags = signal<TagResponse[]>([]);

  /**
   * The tag filter is an enhancement on a page `interaction:read` already
   * entitles the user to, but `/tags/used` behind it needs `tag:read`. Without
   * that permission the filter is hidden and never fetched, instead of
   * answering 403 and putting up a missing-permission notice on every visit.
   *
   * A computed, not a one-off read: permissions arrive with the user fetch,
   * which `authGuard` does not await, so this is routinely false on first run.
   */
  protected readonly canReadTags = computed(() =>
    this._permissionService.hasPermission('tag:read')
  );
  protected readonly selectedTags = computed(() =>
    this.filterTags()
      .filter((t) => this.selectedTagIds().includes(t.id))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  private readonly _hasLoadedData = signal<boolean>(false);

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  private readonly _params$ = combineLatest({
    clientId: this._clientId$,
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    tagIds: toObservable(this.selectedTagIds),
    includeShared: toObservable(this.includeShared),
    __fetch: this._fetch$.pipe(startWith(undefined)),
  }).pipe(
    map(({ clientId, query, sort, keyword, tagIds }) => ({
      ...(clientId ? { clientId } : null),
      ...query,
      ...sort,
      ...(keyword ? { q: keyword.trim() } : null),
      ...(tagIds.length ? { tagIds } : null),
    })),
    debounceTime(0)
  );

  private readonly _paginatedInteractions$ = this._params$.pipe(
    switchMap((params) => {
      const isTemplates = this.currentRoutePath() === 'templates';
      return this._interactionService
        .getAll({
          params: {
            ...params,
            status: ['draft'],
            mode: isTemplates ? 'none' : 'match',
            ...(isTemplates && this.includeShared() ? { includeShared: true } : null),
          },
        })
        .pipe(
          tap({
            error: () =>
              this._dialog.alert({
                title: `Foutmelding`,
                message: `Er is iets misgegaan bij het ophalen van de interacties. Probeer het later nog eens`,
                confirmText: `OK`,
              }),
          }),
          takeUntilDestroyed(this._destroyRef),
          triggerIfSlow(() =>
            this._globalLoaders.setActive(
              ClientInteractionsComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          ),
          finalize(() =>
            this._globalLoaders.setInactive(
              ClientInteractionsComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          )
        );
    }),
    tap(() => this._hasLoadedData.set(true)),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total })),
    catchError(() => EMPTY)
  );

  private readonly _interactions$ = this._paginatedInteractions$.pipe(
    map(({ items }) => items)
  );

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  protected readonly router = inject(Router);

  protected readonly dataSource = toSignal(this._interactions$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly isSearchDisabled = computed(() => !this._hasLoadedData());

  protected readonly statusMapping: Record<
    Exclude<InteractionStatus, 'published'>,
    string
  > = {
    draft: 'Draft',
  };

  protected readonly displayedColumns = computed(() => {
    const base = [
      'label',
      'version',
      'estimatedDuration',
      'createdAt',
      'updatedAt',
      'tags',
    ];

    if (this.currentRoutePath() === 'templates' && this.includeShared()) {
      base.push('sharedBy');
    }

    base.push('actions');
    return base;
  });

  protected readonly pathMap = {
    templates: {
      singular: 'template',
      plural: 'templates',
    },
    interactions: {
      singular: 'interactie',
      plural: 'interacties',
    },
  };

  protected readonly pageTitleMap = {
    templates: capitalize(this.pathMap.templates.plural),
    interactions: capitalize(this.pathMap.interactions.plural),
  };

  protected readonly currentRoutePath = toSignal(
    this._route.url.pipe(
      map((urlSegments) => urlSegments.at(0)),
      filter(Boolean),
      map((segment) => segment.path as 'interactions' | 'templates')
    )
  );

  constructor() {
    combineLatest([
      this._clientId$,
      toObservable(this.includeShared),
      toObservable(this.canReadTags),
      this._fetch$.pipe(startWith(undefined)),
    ])
      .pipe(
        switchMap(([clientId, includeShared, canReadTags]) =>
          canReadTags
            ? this._tagService.getUsedTags(
                clientId
                  ? { clientId }
                  : {
                      mode: 'none',
                      ...(includeShared ? { includeShared: true } : null),
                    }
              )
            : of<TagResponse[]>([])
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((tags) => this.filterTags.set(tags));

    // Auto-refresh on template move events (started + completed)
    this._wsService
      .connect('/event-stream', {
        requireAuth: true,
        topics: ['unit/+/interaction/changed'],
      })
      .pipe(
        switchMap((connection) => connection.events$),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event) => {
        this._fetch$.next();

        // Notify when reuse targets could not be found and were
        // copied instead.
        const payload = event?.payload as
          | { reuse_fallbacks?: string[] }
          | undefined;
        if (
          payload?.reuse_fallbacks &&
          payload.reuse_fallbacks.length > 0
        ) {
          this._snackbar.open(
            'De verplaatsing is voltooid. Sommige templates konden ' +
              'niet worden hergebruikt en zijn in plaats daarvan gekopieerd.',
            'OK',
          );
        }
      });
  }

  getSortedTags(tags: TagResponse[]): TagResponse[] {
    return [...tags].sort((a, b) => a.label.localeCompare(b.label));
  }

  getOverflowTagsTooltip(tags: TagResponse[]): string {
    return tags
      .slice(2)
      .map((t) => t.label)
      .join(', ');
  }

  navigateToInteraction(interactionId: string) {
    const path = this.currentRoutePath();

    if (!path) {
      return;
    }

    this.router.navigate([path, interactionId]);
  }

  handleInteractionAdd() {
    const path = this.currentRoutePath();

    if (!path) {
      return;
    }

    this.router.navigate(['new'], { relativeTo: this._route });
  }

  handleAiAgentChat() {
    const path = this.currentRoutePath();
    if (!path) {
      return;
    }

    const isTemplate = path === 'templates';
    const clientId = isTemplate
      ? null
      : this._route.parent?.snapshot.paramMap.get('clientId') ?? null;

    this.router.navigate([path, 'new', 'flow'], {
      queryParams: {
        ai: 'true',
        ...(clientId ? { clientId } : {}),
      },
    });
  }

  handleInteractionDelete(interaction: Interaction) {
    const path = this.currentRoutePath();

    if (!path) {
      return;
    }

    let message =
      'Als je deze interactie verwijdert, worden alle bijbehorende events en verwijzingen in de signaleringsmodule ook verwijderd. Deze actie kan niet ongedaan worden gemaakt';

    if (path === 'templates') {
      message =
        'Als je deze template verwijdert, is de template niet meer beschikbaar voor het kiezen bij de client interacties. Deze actie kan niet ongedaan worden gemaakt';
    }

    const dialogRef = this._dialog.confirm({
      title: `${interaction.label} verwijderen?`,
      message,
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        of(interaction.correlationId).pipe(
          switchMap((correlationId) =>
            this._interactionService.delete(correlationId).pipe(
              tap(() => {
                adjustPageIfNeeded(this.dataSource, {
                  paginator: this._paginator(),
                  trigger: this._fetch$,
                });

                // Post-action: warn if any folder blocks now have 0 matches
                if (
                  path !== 'templates' &&
                  interaction.clientId &&
                  interaction.tags.length > 0
                ) {
                  this._scmWarningService
                    .checkAndWarn(
                      interaction.clientId,
                      interaction.tags.map((t) => t.id)
                    )
                    .subscribe();
                }
              }),
              catchError((error) => {
                dialogRef.close();

                if (
                  isHttpErrorResponse(error) &&
                  error.status === 409 &&
                  error.error.detail.errorCode === 'INTERACTION_REFERENCED'
                ) {
                  this._showInteractionReferencedError(error);
                } else {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message: `Er is iets misgegaan bij het verwijderen van ${escapeHtml(interaction.label)}. Probeer het later nog eens`,
                    confirmText: 'OK',
                  });
                }

                return EMPTY;
              })
            )
          )
        ),
    });
  }

  handleShare(interaction: Interaction) {
    this._interactionService
      .getById(interaction._id)
      .pipe(
        switchMap((fullInteraction) => {
          const hasMedia = this._interactionHasMedia(fullInteraction);

          const share$ = this._interactionService.share(interaction.correlationId).pipe(
            tap(() => this._fetch$.next()),
            catchError((error) => {
              if (
                isHttpErrorResponse(error) &&
                error.status === 400 &&
                error.error.detail.errorCode === 'SHARE_NOT_PUBLISHED'
              ) {
                this._dialog.alert({
                  title: 'Template niet gepubliceerd',
                  message: `De template "${escapeHtml(interaction.label)}" moet eerst gepubliceerd worden voordat deze gedeeld kan worden.`,
                  confirmText: 'OK',
                });
              } else {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message: `Er is iets misgegaan bij het delen van ${escapeHtml(interaction.label)}. Probeer het later nog eens`,
                  confirmText: 'OK',
                });
              }
              return throwError(() => error);
            })
          );

          if (hasMedia) {
            return new Observable<void>((subscriber) => {
              this._dialog.confirm({
                title: 'Template delen',
                message:
                  'Deze template bevat media. Alle gebruikte media wordt gedeeld samen met de template.',
                confirmText: 'Delen',
                cancelText: 'Annuleren',
                onConfirm: () =>
                  share$.pipe(
                    tap(() => {
                      subscriber.next();
                      subscriber.complete();
                    }),
                    catchError((error) => {
                      subscriber.error(error);
                      return EMPTY;
                    })
                  ),
              });
            });
          }

          return share$;
        })
      )
      .subscribe();
  }

  handleUnshare(interaction: Interaction) {
    const dialogRef = this._dialog.confirm({
      title: `${interaction.label} niet meer delen?`,
      message:
        'Als je stopt met delen, hebben onderliggende units geen toegang meer tot deze template.',
      confirmText: 'Niet meer delen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._interactionService.unshare(interaction.correlationId).pipe(
          tap(() => this._fetch$.next()),
          catchError((error) => {
            dialogRef.close();

            if (
              isHttpErrorResponse(error) &&
              error.status === 409 &&
              error.error.detail.errorCode === 'SHARED_INTERACTION_REFERENCED'
            ) {
              this._showUnshareReferencedError(error);
            } else {
              this._dialog.alert({
                title: 'Foutmelding',
                message: `Er is iets misgegaan bij het stoppen van delen van ${escapeHtml(interaction.label)}. Probeer het later nog eens`,
                confirmText: 'OK',
              });
            }

            return EMPTY;
          })
        ),
    });
  }

  handleMove(interaction: Interaction) {
    if (interaction.moveId) {
      this._dialog.alert({
        title: 'Template wordt verplaatst',
        message:
          'Deze template wordt momenteel verplaatst. Wacht tot de verplaatsing is afgerond.',
        confirmText: 'OK',
      });
      return;
    }

    if (interaction.shareId) {
      this._dialog.alert({
        title: 'Template is gedeeld',
        message:
          'Gedeelde templates kunnen niet worden verplaatst. ' +
          'Verwijder eerst het delen voordat je de template verplaatst.',
        confirmText: 'OK',
      });
      return;
    }

    this.router.navigate(['/templates', interaction.correlationId, 'move']);
  }

  handleInteractionDuplicate(interaction: Interaction) {
    of(interaction._id)
      .pipe(
        switchMap((interactionId) =>
          this._interactionService.duplicate(interactionId).pipe(
            tap(() => this._fetch$.next()),
            catchError((error) => {
              this._dialog.alert({
                title: 'Foutmelding',
                message: `Er is iets misgegaan bij het dupliceren van ${escapeHtml(interaction.label)}. Probeer het later nog eens`,
                confirmText: 'OK',
              });
              return throwError(() => error);
            })
          )
        )
      )
      .subscribe();
  }

  handleInteractionNameEdit(interaction: Interaction) {
    this._handlePromptDialog(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          map((result) => ({
            label: result,
          })),
          switchMap((data) =>
            this._interactionService.update(interaction._id, data).pipe(
              tap(() => this._fetch$.next()),
              catchError((error) => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message: `Er is iets misgegaan bij het bewerken van ${escapeHtml(interaction.label)}. Probeer het later nog eens`,
                  confirmText: 'OK',
                });
                return throwError(() => error);
              })
            )
          )
        ),
      {
        title: 'Naam wijzigen',
        value: interaction.label,
      }
    );
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  handleIncludeSharedChange(value: boolean): void {
    this.includeShared.set(value);
    this._query.update((state) => ({ ...state, page: 1 }));
  }

  handleTagFilterChange(ids: string[]): void {
    this.selectedTagIds.set(ids);
    this._query.update((state) => ({ ...state, page: 1 }));
  }

  removeTag(id: string): void {
    this.selectedTagIds.update((current) => current.filter((i) => i !== id));
    this._query.update((state) => ({ ...state, page: 1 }));
  }

  pickTemplate() {
    this._interactionSelectionService.open(
      (result) =>
        of(result).pipe(
          filter((items): items is Interaction[] => items != null && items.length > 0),
          withLatestFrom(this._clientId$),
          switchMap(([items, clientId]) =>
            this._interactionService
              .attachTemplatesToClient({
                clientId: clientId as string,
                templateIds: items.map((i) => i._id),
              })
              .pipe(
                tap(() => this._fetch$.next()),
                catchError((error) => {
                  // Opnieuw proberen helpt hier niet: de template zelf moet
                  // eerst opnieuw gepubliceerd worden door de afdeling die
                  // hem deelt. Daarom een eigen uitleg in plaats van de
                  // algemene "probeer het later nog eens".
                  if (
                    isHttpErrorResponse(error) &&
                    error.error?.detail?.errorCode === 'TEMPLATE_GOTO_NOT_ACCESSIBLE'
                  ) {
                    const label = error.error.detail.metadata?.['templateLabel'] as
                      | string
                      | undefined;

                    this._dialog.alert({
                      title: 'Template kan nog niet gekoppeld worden',
                      message: `De template ${
                        label ? `'${label}'` : 'die u koos'
                      } gaat verder naar een andere template die niet met uw afdeling is gedeeld. Daarom kan hij nu niet gekoppeld worden.<br /><br />Vraag de afdeling die deze template met u deelt om hem opnieuw te publiceren. Daarna kunt u het koppelen opnieuw proberen.`,
                      confirmText: 'OK',
                    });
                    return throwError(() => error);
                  }

                  this._dialog.alert({
                    title: 'Foutmelding',
                    message: `Er is iets misgegaan bij het koppelen van deze template aan de cliënt. Probeer het later nog eens`,
                    confirmText: 'OK',
                  });
                  return throwError(() => error);
                })
              )
          )
        ),
      {
        status: ['published'],
        mode: 'none',
        includeShared: true,
        storageKey: 'template-interaction-selection',
      }
    );
  }

  private _showUnshareReferencedError(error: HttpErrorResponse): void {
    const references = (error.error.detail.metadata?.['references'] ?? []) as {
      label: string;
    }[];

    this._dialog.open<ReferencedDialogComponent, ReferencedDialogData>(
      ReferencedDialogComponent,
      {
        title: 'Template kan niet worden gestopt met delen',
        message:
          'Deze template kan niet worden gestopt met delen omdat de volgende gedeelde templates er nog naar verwijzen via een ga-naar-interactie actie:',
        path: this.currentRoutePath() ?? 'templates',
        references: references.map(({ label }) => ({
          label,
          hint: 'Stop eerst met delen van deze template, of verwijder de ga-naar-interactie actie.',
          draftId: null,
        })),
      }
    );
  }

  private _showInteractionReferencedError(error: HttpErrorResponse): void {
    const references = (error.error.detail.metadata?.['references'] ?? []) as {
      label: string;
      statuses: string[];
      draft_id: string | null;
    }[];

    const path = this.currentRoutePath() ?? 'interactions';

    this._dialog.open<ReferencedDialogComponent, ReferencedDialogData>(
      ReferencedDialogComponent,
      {
        title: 'Interactie kan niet worden verwijderd',
        message:
          'Deze interactie kan niet worden verwijderd. Er zijn nog verwijzingen vanuit:',
        path,
        references: references.map(({ label, statuses, draft_id }) => {
          const hasPublished = statuses.includes('published');
          const hasDraft = statuses.includes('draft');

          let hint: string;

          if (hasPublished && hasDraft) {
            hint = 'Verwijder de actie uit het concept en publiceer een nieuwe versie.';
          } else if (hasPublished) {
            hint = 'Publiceer een bijgewerkte versie zonder deze verwijzing.';
          } else {
            hint = "Verwijder de 'Ga naar interactie'-actie uit het concept.";
          }

          return { label, hint, draftId: draft_id };
        }),
      }
    );
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }

  private _handlePromptDialog(
    callback: ConfirmHandler<string>,
    options: { title: string; value?: string }
  ) {
    const path = this.currentRoutePath() ?? 'interactions';

    return this._dialog.prompt(
      {
        title: options?.title,
        message: '',
        confirmText: 'Opslaan',
        cancelText: 'Annuleren',
        onConfirm: callback,
        input: {
          mode: 'input',
          placeholder: `Naam ${this.pathMap[path].singular}`,
          defaultValue: options?.value,
          validators: {
            required: {
              validator: Validators.required,
              errorMessage: 'Vul een naam in',
            },
            pattern: {
              validator: Validators.pattern(/^[^\\/:*?"<>|]*$/),
              errorMessage:
                'De volgende tekens zijn niet toegestaan: \\ / : * ? " < > |',
            },
            maxlength: {
              validator: Validators.maxLength(100),
              errorMessage: (args) => {
                const errors = args as ValidationErrors;

                return `Maximaal ${errors['requiredLength']} karakters`;
              },
            },
          },
        },
      },
      {
        maxWidth: '396px',
        width: '100%',
      }
    );
  }

  private _interactionHasMedia(interaction: Interaction): boolean {
    const hasAudioAction = interaction.actions.some((a) => a.type === 'audio');
    const hasImageAnswer = interaction.answers.some((a) => a.type === 'image');

    return hasAudioAction || hasImageAnswer;
  }
}
